import { User } from "@/types/user";
import axios from "axios";
import EventEmitter from "events";
import { PresenceChannel } from "laravel-echo";
import { useEffect } from "react";

export type RoomUser = {
    id: number;
    type: string;
};

type UserKey = `${string}_${string | number}`;

type SignalingMessage =
    | {
          from: UserKey;
          to: UserKey;
          type: "offer" | "answer";
          data: RTCSessionDescriptionInit;
      }
    | {
          from: UserKey;
          to: UserKey;
          type: "ice-candidate";
          data: RTCIceCandidate;
      };

export type WebRTCOptions<DataMessage extends { type: string }> = {
    onMessage?: (userKey: UserKey, message: DataMessage) => void;
    onRoomJoined?: (users: RoomUser[]) => void;
    onRoomLeft?: () => void;
    onUserJoined?: (user: RoomUser) => void;
    onUserLeft?: (user: RoomUser) => void;
    onConnectionStateChange?: (
        userKey: UserKey,
        state: "open" | "closed",
    ) => void;
};

export class WebRTC<DataMessage extends { type: string }> {
    myUserKey: UserKey | null = null;
    roomType: string | null = null;
    roomId: string | number | null = null;

    messageEventEmitter: EventEmitter;

    onMessage?: (userKey: UserKey, message: DataMessage) => void;
    onRoomJoined?: (users: RoomUser[]) => void;
    onRoomLeft?: () => void;
    onUserJoined?: (user: RoomUser) => void;
    onUserLeft?: (user: RoomUser) => void;
    onConnectionStateChange?: (
        userKey: UserKey,
        state: "open" | "closed",
    ) => void;

    isInRoom: boolean;
    roomUsers: RoomUser[];

    channel: PresenceChannel | null;
    peerConnections: Map<UserKey, RTCPeerConnection>;
    dataChannels: Map<UserKey, RTCDataChannel>;
    iceCandidates: Map<UserKey, RTCIceCandidate[]>;

    constructor({
        onMessage,
        onRoomJoined,
        onRoomLeft,
        onUserJoined,
        onUserLeft,
        onConnectionStateChange,
    }: WebRTCOptions<DataMessage>) {
        this.messageEventEmitter = new EventEmitter();

        this.onMessage = onMessage;
        this.onRoomJoined = onRoomJoined;
        this.onRoomLeft = onRoomLeft;
        this.onUserJoined = onUserJoined;
        this.onUserLeft = onUserLeft;
        this.onConnectionStateChange = onConnectionStateChange;

        this.isInRoom = false;
        this.roomUsers = [];

        this.channel = null;
        this.peerConnections = new Map();
        this.dataChannels = new Map();
        this.iceCandidates = new Map();
    }

    getUserKey(user: RoomUser): UserKey {
        return `${user.type}_${user.id}`;
    }

    isInitiator(userKey: UserKey): boolean {
        return !!this.myUserKey && this.myUserKey > userKey;
    }

    async getCombinedConnectionData(): Promise<RTCConfiguration> {
        console.info(
            `[${this.myUserKey}] Using combined STUN and TURN configuration`,
        );

        const stunServers = [
            {
                urls: [
                    "stun:stun.l.google.com:19302",
                    "stun:stun1.l.google.com:19302",
                    "stun:stun2.l.google.com:19302",
                    "stun:stun3.l.google.com:19302",
                    "stun:stun4.l.google.com:19302",
                ],
            },
        ];

        try {
            // Get TURN servers from API
            const { data: turnConfig } = await axios.get(
                route("turn.credentials"),
            );

            // Combine STUN and TURN servers
            return {
                iceServers: [...stunServers, ...(turnConfig.iceServers || [])],
            };
        } catch (error) {
            console.warn(
                `[${this.myUserKey}] Failed to get TURN credentials, falling back to STUN only:`,
                error,
            );
            // Fallback to STUN only if TURN credentials fail
            return {
                iceServers: stunServers,
            };
        }
    }

    async createPeerConnection(userKey: UserKey) {
        console.info(
            `[${this.myUserKey}] Creating peer connection for ${userKey}`,
        );
        if (this.peerConnections.has(userKey)) {
            this.peerConnections.get(userKey)?.close();
            this.peerConnections.delete(userKey);
        }
        const pc = new RTCPeerConnection(
            await this.getCombinedConnectionData(),
        );
        pc.onicecandidate = (event) => {
            console.info(`[${this.myUserKey}] ICE candidate:`, event.candidate);
            this.channel?.whisper("signal", {
                from: this.myUserKey,
                to: userKey,
                type: "ice-candidate",
                data: event.candidate,
            } as SignalingMessage);
        };
        if (this.isInitiator(userKey)) {
            const dc = pc.createDataChannel("data");
            this.setUpDataChannel(userKey, dc);
            this.createOffer(userKey, pc);
        } else {
            pc.ondatachannel = (event) => {
                const dc = event.channel;
                this.setUpDataChannel(userKey, dc);
            };
        }
        this.peerConnections.set(userKey, pc);
        return pc;
    }

    setUpDataChannel(userKey: UserKey, dc: RTCDataChannel) {
        dc.onmessage = (event) => {
            const message = JSON.parse(event.data) as DataMessage;
            this.messageEventEmitter.emit(message.type, userKey, message);
            this.onMessage?.(userKey, message);
        };
        dc.onopen = () => {
            console.info(`[${this.myUserKey}] Data channel opened`);
            this.onConnectionStateChange?.(userKey, "open");
        };
        dc.onclose = () => {
            console.info(`[${this.myUserKey}] Data channel closed`);
            this.dataChannels.delete(userKey);
            this.iceCandidates.delete(userKey);
            this.onConnectionStateChange?.(userKey, "closed");
        };
        this.dataChannels.set(userKey, dc);
    }

    useMessageEvent<T extends DataMessage["type"]>(
        type: T,
        callback: (
            userKey: UserKey,
            message: Extract<DataMessage, { type: T }>,
        ) => void,
    ) {
        useEffect(() => {
            this.messageEventEmitter.on(type, callback);
            return () => {
                this.messageEventEmitter.off(type, callback);
            };
        }, [type, callback]);
    }

    async createOffer(userKey: UserKey, pc: RTCPeerConnection) {
        console.info(`[${this.myUserKey}] Creating offer for ${userKey}`);
        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);
        this.channel?.whisper("signal", {
            from: this.myUserKey,
            to: userKey,
            type: "offer",
            data: offer,
        } as SignalingMessage);
    }

    async handleOffer(message: SignalingMessage) {
        if (message.type !== "offer") return;
        console.info(`[${this.myUserKey}] Received offer from ${message.from}`);
        const pc = await this.createPeerConnection(message.from);
        pc.setRemoteDescription(message.data);

        const currentCandidates = this.iceCandidates.get(message.from) ?? [];
        currentCandidates.forEach((candidate) => {
            pc.addIceCandidate(candidate);
        });

        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);

        console.info(`[${this.myUserKey}] Sending answer to ${message.from}`);
        this.channel?.whisper("signal", {
            from: this.myUserKey,
            to: message.from,
            type: "answer",
            data: pc.localDescription,
        } as SignalingMessage);
    }

    async handleAnswer(message: SignalingMessage) {
        if (message.type !== "answer") return;
        console.info(
            `[${this.myUserKey}] Received answer from ${message.from}`,
        );
        const pc = this.peerConnections.get(message.from);
        if (pc) {
            pc.setRemoteDescription(message.data);
        } else {
            console.error(
                `[${this.myUserKey}] Got answer from ${message.from} but no peer connection found`,
            );
        }
    }

    handleIceCandidate(message: SignalingMessage) {
        if (message.type !== "ice-candidate") return;
        console.info(
            `[${this.myUserKey}] Received ICE candidate from ${message.from}`,
        );
        const pc = this.peerConnections.get(message.from);
        if (pc) {
            pc.addIceCandidate(message.data);
        } else {
            console.info(
                `[${this.myUserKey}] Got ICE candidate from ${message.from} but no peer connection found. Storing for later.`,
            );
            const currentCandidates =
                this.iceCandidates.get(message.from) ?? [];
            this.iceCandidates.set(message.from, [
                ...currentCandidates,
                message.data,
            ]);
        }
    }

    /**
     * @returns how many peers the message actually reached. Callers that show
     * the user something on the strength of a send need this — an open room
     * with no open data channel is silently a no-op otherwise.
     */
    sendMessage(message: DataMessage): number {
        let delivered = 0;
        this.dataChannels.forEach((dc) => {
            if (dc.readyState === "open") {
                dc.send(JSON.stringify(message));
                delivered++;
            }
        });
        return delivered;
    }

    sendMessageTo(userKey: UserKey, message: DataMessage) {
        const dc = this.dataChannels.get(userKey);
        if (dc) {
            dc.send(JSON.stringify(message));
        }
    }

    joinRoom({
        user,
        roomType,
        roomId,
    }: {
        user: User;
        roomType: string;
        roomId: string | number;
    }) {
        if (this.channel) {
            throw new Error(
                "Already in room. Must leave before joining again.",
            );
        }
        this.myUserKey = `${user.type}_${user.id}`;
        this.roomType = roomType;
        this.roomId = roomId;

        this.channel = window.Echo.join(
            `webrtc.${this.roomType}.${this.roomId}`,
        );
        this.channel
            .here((users: RoomUser[]) => {
                this.isInRoom = true;
                this.roomUsers = users;

                console.info(
                    `[${this.myUserKey}] Joined room with users:`,
                    users.map((u) => this.getUserKey(u)),
                );

                if (this.onRoomJoined) {
                    this.onRoomJoined(users);
                }

                // Initiate connections with existing users
                users.forEach((user) => {
                    const userKey = this.getUserKey(user);
                    if (userKey === this.myUserKey) return;
                    if (this.isInitiator(userKey)) {
                        this.createPeerConnection(userKey);
                    } else {
                        console.info(
                            `[${this.myUserKey}] User ${userKey} is present - waiting for them to initiate`,
                        );
                    }
                });
            })
            .joining((user: RoomUser) => {
                this.roomUsers = [...this.roomUsers, user];
                const userKey = this.getUserKey(user);

                console.info("user joined", user);

                if (this.onUserJoined) {
                    this.onUserJoined(user);
                }
                if (this.isInitiator(userKey)) {
                    this.createPeerConnection(userKey);
                } else {
                    console.info(
                        `[${this.myUserKey}] User ${userKey} joined - waiting for them to initiate`,
                    );
                }
            })
            .leaving((user: RoomUser) => {
                const userKey = this.getUserKey(user);
                this.roomUsers = this.roomUsers.filter(
                    (u) => this.getUserKey(u) !== userKey,
                );

                console.info(
                    `[${this.myUserKey}] User ${userKey} left - cleaning up connection`,
                );

                if (this.onUserLeft) {
                    this.onUserLeft(user);
                }

                // Immediately close and remove the peer connection to prevent stale connections
                const pc = this.peerConnections.get(userKey);
                if (pc) {
                    // Force close the connection
                    pc.close();
                    console.info(
                        `[${this.myUserKey}] Force closed connection to ${userKey}`,
                    );
                    this.peerConnections.delete(userKey);
                    this.iceCandidates.delete(userKey);
                }
            })
            .listenForWhisper("signal", async (message: SignalingMessage) => {
                if (message.from === this.myUserKey) return;
                if (message.to !== this.myUserKey) return;
                if (message.type === "offer") this.handleOffer(message);
                if (message.type === "answer") this.handleAnswer(message);
                if (message.type === "ice-candidate")
                    this.handleIceCandidate(message);
            })
            .error((error: Error) => {
                console.error("Room error:", error);
                throw error;
            });
    }

    leaveRoom() {
        window.Echo.leave(`webrtc.${this.roomType}.${this.roomId}`);
        this.channel = null;
        this.peerConnections.forEach((pc) => pc.close());
        this.peerConnections.clear();
        this.iceCandidates.clear();
        this.dataChannels.forEach((dc) => dc.close());
        this.dataChannels.clear();
        this.messageEventEmitter.removeAllListeners();
        this.onRoomLeft?.();
        this.isInRoom = false;
        this.roomUsers = [];
    }
}
